import { MainHeader, Section, SectionParagraph } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { PartnerLogo } from '@/components/shared/PartnerLogo';
import { useGetAuthUser } from '@/hooks/useGetAuthUser';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { useAnalyticsContext, useI18nContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import type React from 'react';
import { scroller } from 'react-scroll';
import styled from 'styled-components';

const TopFoldSection = styled(Section)<{ withSecondaryCta: boolean }>`
  position: relative;
  height: 617px;
  z-index: 1;
  background-color: transparent;

  &:before,
  &:after {
    z-index: -1;
  }

  ${MEDIA_QUERIES.mobile<{ withSecondaryCta: boolean }>`
        background: linear-gradient(to top, ${themedColor('cloud')} 27px,
            transparent 27px);
        height: ${({ withSecondaryCta }) => (withSecondaryCta ? '675px' : '617px')};
    `}
`;

const TopFoldTitle = styled(MainHeader)`
  white-space: initial;
  ${MEDIA_QUERIES.desktop`
    white-space: pre;
  `}
`;

const MobileBackToQuote = styled.a`
  display: none;

  ${MEDIA_QUERIES.mobile`
        display: flex;
        color: ${themedColor('primary')};
        ${font('main', {
          fontSize: '14px',
          lineHeight: 'normal',
          fontWeight: 400,
        })};
        letter-spacing: normal;
        padding: 9px 18px 12px 18px;
        box-shadow: 0 47px 46px -27px rgba(0, 0, 0, 0.16), 0 2px 12px rgba(0, 0, 0, 0.06);
        border-radius: 100px;
        margin-bottom: 19px;
        width: 180px;
        height: 38px;
        background: transparent url("/pink-right-arrow.svg") no-repeat;
        background-position: 148px 11px;
        -webkit-tap-highlight-color: transparent;
        margin-top: -20px;

        html[lang='de-DE'] & {
            width: 230px;
            background: transparent url("/arrow_right_red.svg") no-repeat;
            background-size: 15px 15px;
            background-position: 197px 11px;
        }

        html[lang='en-DE'] & {
            background: transparent url("/arrow_right_red.svg") no-repeat;
            background-size: 15px 15px;
            background-position: 148px 11px;
        }
    `};
`;

const Actions = styled(Flex)`
  z-index: 1;
`;

const CTA = styled(JoinCTA)`
  &&& {
    display: block;
    padding: 18px 44px;
    font-size: 1.8rem;
  }
`;

const SecondaryCta = styled.a`
  margin-top: 10px;
  color: ${themedColor('darkGray')};
  ${font('main', { fontSize: '14px', lineHeight: '28px' })};
  text-align: center;
  cursor: pointer;
  ${MEDIA_QUERIES.mobile`
            width: 300px
        `}
`;

interface CustomBackgroundProps {
  image: string;
  homePageGreybox: boolean;
}

export const CustomBackground = styled.div<CustomBackgroundProps>`
  position: absolute;
  height: 100%;
  width: 100vw;
  background-image: url(${(props): string => `${props.image}`});
  background-size: cover;
  background-position: right;
  background-repeat: no-repeat;
  display: block;
  flex: 1;
  z-index: -1;
  bottom: 0px;

  ${MEDIA_QUERIES.tabletAndDesktop`
      height: 100%;
      background-size: contain;
      background-position: bottom center;
    `}
`;

export interface HeroProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  readonly secondaryCta?: {
    label: string;
    scrollToSection: string;
  };
  readonly title: string;
  readonly subtitle: string;
  readonly ctaLabel: string;
  readonly ctaUrl?: string;
  readonly cart: {
    readonly singular: string;
    readonly plural: string;
  };
  readonly partnerLogo?: string;
  readonly bgImage?: string;
  readonly homePageGreybox?: boolean;
}

export const Hero: React.FC<HeroProps> = ({
  secondaryCta,
  title,
  subtitle,
  ctaLabel,
  cart,
  ctaUrl,
  partnerLogo,
  bgImage,
  homePageGreybox = false,
}) => {
  const { user } = useGetAuthUser();
  const { locale } = useI18nContext();
  const { ref: mainCtaRef } = useShowHeaderCtaOnScroll();
  const { trackEvent } = useAnalyticsContext();

  const showQuotePath = user?.showCart && user?.quotePath != null && user?.userFirstName != null;

  const scrollToSection = (place: string): void => {
    scroller.scrollTo(place, {
      duration: 1100,
      smooth: true,
      offset: -30,
    });
    trackEvent(`switching_scroll_link_${locale}_clicked`);
  };

  return (
    <>
      <PartnerLogo src={partnerLogo} />
      <TopFoldSection px={20} pt={[15, 27, 80]} pb={[140, 201, 266]} withSecondaryCta={secondaryCta != null}>
        {bgImage && <CustomBackground image={bgImage} homePageGreybox={homePageGreybox} />}
        {showQuotePath && <MobileBackToQuote href={user.quotePath}>{cart.plural}</MobileBackToQuote>}

        {user?.showCart && (
          <MobileBackToQuote href={user?.cartPath}>
            {(user?.cartCount ?? 0) > 1 ? cart.plural : cart.singular}
          </MobileBackToQuote>
        )}

        <Flex gap="20px" flexDirection="column" justifyContent="center">
          <TopFoldTitle>{parse(title)}</TopFoldTitle>
          <SectionParagraph>{parse(subtitle)}</SectionParagraph>
        </Flex>

        <Actions flexDirection="column" alignItems="center">
          <Box mt={[30, 45, 75]}>
            <CTA
              ref={mainCtaRef}
              flavor="primary"
              glowing
              aria-label={ctaLabel}
              id="gtm_button_main_check_prices"
              data-source="top"
              customUrl={ctaUrl}
            >
              {ctaLabel}
            </CTA>
          </Box>
          {secondaryCta && (
            <SecondaryCta onClick={() => scrollToSection(secondaryCta.scrollToSection)}>
              {secondaryCta.label}
            </SecondaryCta>
          )}
        </Actions>
      </TopFoldSection>
    </>
  );
};
